import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AgeingBar, Badge, cx, rowLinkClass, Td, Th, type Tone } from '@timely/ui';
import {
    ACTIVITY_LABELS, RANK_WORDS, ageingWindowRows, daysFromToday, followUpWords, reasonTone,
    type AttentionTone, type BookRow, type FollowUpTone, type PriorityReason,
} from '@timely/domain';
import { useFormatters } from './useFormatters';
import type { LastNote } from './useBook';
import { accountCode } from './accountCode';

/**
 * The list of accounts that Today and Customers share (PRODUCT-UX-FOUNDATION
 * §7–8, DESIGN-SYSTEM §9): one structure, so the two can never show an account
 * differently.
 *
 * A real table — the header and the cells share their columns by construction
 * — that lays itself out by its own width (container queries), because the
 * same list sits full width in Customers and beside a side column on Today:
 *
 *   - from 720 px of list: Customer, Outstanding, Over 90 days old, Next
 *     follow-up, Promised;
 *   - from 896 px: Last contact too, with what was last said under it;
 *   - from 1056 px, for somebody who sees others' accounts: Assigned to;
 *   - from 1280 px: Last contact widens, so the last note reads in full;
 *   - from 1408 px: the age of the money as a bar;
 *   - narrower than 720 px, each account is two lines — name and amount, then
 *     what is next — because a squeezed six-column table helps nobody.
 *
 * A column that does not fit yet rides in the line under the name, or waits
 * for the account's own page. Every row is one link, stretched over the row,
 * with a short spoken summary: the row is the thing somebody presses forty
 * times a morning, and it is one tab stop. When the list offers a choice of
 * rows (Customers, for somebody who may act on many), a checkbox sits before
 * the name, above the row's link.
 *
 * Nothing here filters, sorts or decides who may see what: the rows are what
 * the database returned for this person.
 *
 * (Tailwind finds classes by reading the source, so every width variant below
 * is written out in full rather than assembled.)
 */
export interface AccountTableProps {
    rows: BookRow[];
    /** Where a row goes. */
    linkTo: (customerId: string) => string;
    /** Who the account is assigned to, by name; absent when unknown. */
    assigneeOf: (row: BookRow) => string | null;
    /** The column is noise for somebody whose accounts are all their own. */
    showAssignee: boolean;
    /** Names the table for a screen reader, e.g. "Late accounts". */
    label: string;
    /** What was last said on each account, when the list has it. */
    lastNotes?: Map<string, LastNote>;
    /**
     * Why each account is worth a call today (Today's queue): its stated
     * reasons, most serious first. The first two ride under the name.
     */
    reasonsOf?: (row: BookRow) => PriorityReason[];
    /** Rows that can be chosen, and which are. */
    selection?: {
        chosen: ReadonlySet<string>;
        onToggle: (customerId: string, chosen: boolean) => void;
        onToggleShown: (chosen: boolean) => void;
    };
}

/* Below 720 px of list a row is a two-line block, not a table row. */
const NARROW_ROW = '@max-[45rem]:flex @max-[45rem]:flex-wrap @max-[45rem]:items-baseline @max-[45rem]:gap-x-3 @max-[45rem]:gap-y-1 @max-[45rem]:px-4 @max-[45rem]:py-3 @max-[45rem]:border-b @max-[45rem]:border-separator';
const NARROW_CELL = '@max-[45rem]:!p-0 @max-[45rem]:!h-auto @max-[45rem]:!border-0';

export function AccountTable({ rows, linkTo, assigneeOf, showAssignee, label, lastNotes, reasonsOf, selection }: AccountTableProps) {
    const shown = rows.map(r => r.customer.id);
    const chosenShown = selection ? shown.filter(id => selection.chosen.has(id)).length : 0;
    const all = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (all.current) all.current.indeterminate = chosenShown > 0 && chosenShown < shown.length;
    }, [chosenShown, shown.length]);

    return (
        <div className="@container">
            <table aria-label={label} className="w-full border-collapse text-body [&_tbody_tr:last-child>*]:border-b-0 @max-[45rem]:block @min-[45rem]:table-fixed">
                <thead className="hidden @min-[45rem]:table-header-group">
                    <tr>
                        {selection && (
                            <Th className="w-11 !pr-0">
                                <input
                                    ref={all}
                                    type="checkbox"
                                    aria-label={`Choose all ${shown.length} accounts shown`}
                                    checked={shown.length > 0 && chosenShown === shown.length}
                                    onChange={e => selection.onToggleShown(e.target.checked)}
                                />
                            </Th>
                        )}
                        <Th>Customer</Th>
                        {showAssignee && <Th hideBelow="xl" className="w-36 @min-[88rem]:w-44">Assigned to</Th>}
                        <Th numeric className="w-24 @min-[88rem]:w-32">Outstanding</Th>
                        {/* An age, not overdue: money older than 90 days from its bill (Customer 360, F2). */}
                        <Th numeric className="w-28 @min-[88rem]:w-32">Over 90 days old</Th>
                        <Th className="hidden @min-[88rem]:table-cell w-32 !pl-6">Age of money</Th>
                        <Th hideBelow="lg" className="w-40 @min-[80rem]:w-72 @min-[100rem]:w-96 !pl-6">Last contact</Th>
                        <Th className="w-36 @min-[88rem]:w-40 !pl-6">Next follow-up</Th>
                        <Th numeric className="w-32 @min-[88rem]:w-44">Promised</Th>
                    </tr>
                </thead>
                <tbody className="@max-[45rem]:block">
                    {rows.map(row => (
                        <AccountRow key={row.customer.id} row={row} to={linkTo(row.customer.id)} assignee={assigneeOf(row)}
                            showAssignee={showAssignee} note={lastNotes?.get(row.customer.id) ?? null}
                            reasons={reasonsOf ? reasonsOf(row) : null} selection={selection} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AccountRow({ row, to, assignee, showAssignee, note, reasons, selection }: {
    row: BookRow; to: string; assignee: string | null; showAssignee: boolean;
    note: LastNote | null; reasons: PriorityReason[] | null; selection: AccountTableProps['selection'];
}) {
    const { money, date, today } = useFormatters();
    const c = row.customer;
    const next = nextFollowUp(row, date.short);
    const contacted = lastContact(c.lastFollowUpOn, today, date.short);
    const promised = row.promise ? {
        amount: money.compact(row.promise.amount),
        by: c.forecastDate ? date.short(c.forecastDate) : null,
        window: row.promise.window,
    } : null;
    const said = note ? noteText(note) : null;
    const chosen = selection?.chosen.has(c.id) ?? false;
    // A TallyPrime company's own windows, as every ageing screen shows them (D3, docs/tally/18 §1.3); a sheet's four bands otherwise.
    const windows = c.ageing?.windows ? ageingWindowRows(c.ageing.windows) : null;
    const owes = c.totalType !== 'Cr' && (Number(c.total) || 0) > 0;
    const ages = !owes ? [] : windows ? windows.map(w => w.amount) : [row.overdue.a1, row.overdue.a2, row.overdue.a3, row.overdue.a4];
    const ageWords = windows
        ? windows.map(w => `${w.label} ${money.compact(w.amount)}`).join(', ')
        : `1–45 days ${money.compact(row.overdue.a1)}, 46–90 ${money.compact(row.overdue.a2)}, 91–135 ${money.compact(row.overdue.a3)}, over 135 ${money.compact(row.overdue.a4)}`;

    const spoken = [
        c.company,
        `${money.exact(c.total)} outstanding`,
        row.overdue.over90 > 0 ? `${money.exact(row.overdue.over90)} more than 90 days old` : null,
        `follow-up ${next.spoken}`,
        promised ? `promised ${promised.amount}${promised.by ? ` by ${promised.by}` : ''}${promised.window === 'passed' ? ', date passed' : ''}` : null,
        c.promiseOutcome === 'not_kept' ? 'last promise not kept' : null,
        c.isUrgent ? 'urgent' : null,
        reasons?.length ? `why: ${reasons.slice(0, 2).map(r => r.text).join('; ')}` : null,
    ].filter(Boolean).join(', ');
    const why = reasons?.length
        ? reasons.slice(0, 2).map(r => r.text).join(' · ') + (reasons.length > 2 ? ` · ${reasons.length - 2} more` : '')
        : null;
    const whyTone = reasons?.length ? reasonTone(reasons[0]!) : null;

    // The flags beside the name, most serious first. A "Promise not kept" the reason already says is not said twice.
    const flags: { text: string; tone: Tone }[] = [
        ...(row.badDebt ? [{ text: RANK_WORDS.Bad, tone: 'dang' as const }] : []),
        ...(c.promiseOutcome === 'not_kept' && !row.promise && !reasons?.some(r => r.code === 'promise_not_kept')
            ? [{ text: 'Promise not kept', tone: 'dang' as const }] : []),
        ...(c.isUrgent ? [{ text: 'Urgent', tone: 'dang' as const }] : []),
        ...(c.totalType === 'Cr' ? [{ text: 'In credit', tone: 'neutral' as const }] : []),
        ...(row.settled ? [{ text: 'Settled', tone: 'pos' as const }] : []),
    ];
    // One red mark a row at most (Customer 360, 02 §6): the most serious keeps crimson; the rest say it in words.
    let reds = whyTone === 'crimson' ? 1 : 0;
    const badges = flags.map(f => (f.tone !== 'dang' ? f : reds++ === 0 ? f : { ...f, tone: 'neutral' as const }));

    return (
        <tr className={cx('relative hover:bg-hover focus-within:bg-hover', chosen && 'bg-accent-tint', NARROW_ROW)}>
            {selection && (
                <Td className={cx('!pr-0 w-11 @max-[45rem]:order-first', NARROW_CELL)}>
                    {/* Above the row's stretched link, so pressing the box chooses rather than opens. */}
                    <label className="relative z-10 inline-flex items-center justify-center h-9 w-9 -m-2 cursor-pointer">
                        <input
                            type="checkbox"
                            aria-label={`Choose ${c.company}`}
                            checked={chosen}
                            onChange={e => selection.onToggle(c.id, e.target.checked)}
                        />
                    </label>
                </Td>
            )}
            <Td className={cx('h-13 @max-[45rem]:flex-1 @max-[45rem]:basis-3/5 @max-[45rem]:min-w-0', NARROW_CELL)}>
                <span className="flex items-center flex-wrap gap-x-2 gap-y-1 min-w-0">
                    <Link to={to} aria-label={spoken} className={cx('text-body font-semibold text-label truncate max-w-full', rowLinkClass)}>
                        {c.company}
                    </Link>
                    {badges.map(b => <Badge key={b.text} tone={b.tone}>{b.text}</Badge>)}
                </span>
                <span className="block text-caption text-label-3 truncate mt-0.5">
                    {[accountCode(c.sourceKey), c.city].filter(Boolean).join(' · ') || c.contactPerson || '—'}
                    {/* Until the list is wide enough for its own column, whose it is rides here. */}
                    {showAssignee && assignee && <span className="@min-[66rem]:hidden"> · {assignee}</span>}
                    {/* And what was last said, until Last contact has its own column. */}
                    {said && <span className="hidden @min-[45rem]:inline @min-[56rem]:hidden"> · {said}</span>}
                </span>
                {why && whyTone && (
                    <span className="block text-caption text-label-2 mt-0.5 line-clamp-2" title={reasons!.map(r => r.text).join('\n')}>
                        {/* What the reason is, as the square's colour (a failure, something waiting, an age) and a word for the
                            ear; the words stay ink, so a queue of serious accounts is not a page of red. */}
                        <span aria-hidden="true" className={cx('inline-block size-1.5 mr-1.5 align-middle', REASON_MARK[whyTone])} />
                        <span className="sr-only">Why this account: </span>{why}
                    </span>
                )}
            </Td>

            {showAssignee && (
                <Td hideBelow="xl" className={cx('text-secondary truncate', assignee ? 'text-label-2' : 'text-label-3')}>
                    {assignee ?? 'Unassigned'}
                </Td>
            )}

            <Td numeric className={cx('font-semibold text-label @max-[45rem]:ml-auto', NARROW_CELL)}>
                <span title={money.balance(c.total, c.totalType)}>{money.compact(c.total)}</span>
            </Td>

            {/* The two-line layout breaks here: what follows is the second line. */}
            <td aria-hidden="true" className="basis-full h-0 p-0 @min-[45rem]:hidden" />

            {/* An age, in ink: how old the money is is not how late it is (Customer 360, F2). */}
            <Td numeric className={cx('text-secondary', NARROW_CELL, row.overdue.over90 > 0 ? 'text-label-2' : 'text-label-3 @max-[45rem]:hidden')}>
                {row.overdue.over90 > 0 ? <>{money.compact(row.overdue.over90)}<span className="@min-[45rem]:hidden"> over 90 days old</span></> : '—'}
            </Td>

            <Td className="hidden @min-[88rem]:table-cell !pl-6" title={ageWords}>
                {ages.every(a => a <= 0)
                    ? <span className="text-secondary text-label-3">—</span>
                    : <AgeingBar parts={ages} label={`How old the money is: ${ageWords}`} className="!h-2" />}
            </Td>

            {/* On the second line when narrow; its own column from 896 px of list; between the two it gives way to what is next. */}
            <Td
                className={cx('text-secondary text-label-3 !pl-6 @max-[45rem]:order-last @min-[45rem]:hidden @min-[56rem]:table-cell', NARROW_CELL)}
                title={c.lastFollowUpOn ? date.long(c.lastFollowUpOn) : undefined}
            >
                <span className="@min-[45rem]:hidden">Last contact </span>{contacted}
                {said && (
                    <span className="hidden @min-[56rem]:block text-caption text-label-2 truncate mt-0.5" title={note?.body || undefined}>
                        {said}
                    </span>
                )}
            </Td>

            <Td className={cx('text-secondary whitespace-nowrap !pl-6', NARROW_CELL, NEXT_TONE[next.tone])}>
                <span className="@min-[45rem]:hidden">Next: </span>{next.text}
            </Td>

            {/* A promise waiting for its answer is amber, past its date or due today; crimson waits for "not kept". */}
            <Td numeric title={promised?.window === 'passed' ? 'Promise date passed' : undefined} className={cx('text-secondary', NARROW_CELL, !promised && '@max-[45rem]:hidden',
                promised?.window === 'passed' || promised?.window === 'today' ? 'text-warn' : promised ? 'text-label-2' : 'text-label-3')}>
                {promised
                    ? <><span className="@min-[45rem]:hidden">Promised </span>{promised.amount}{promised.by && <span className="text-label-3 font-normal"> by {promised.by}</span>}</>
                    : '—'}
            </Td>
        </tr>
    );
}

/** A late follow-up is amber, never crimson: crimson is money past its due date (Customer 360, F1). */
const NEXT_TONE: Record<FollowUpTone, string> = {
    late: 'text-warn font-semibold', today: 'text-warn font-semibold', collected: 'text-pos', scheduled: 'text-label-2', not_set: 'text-label-3',
};

const REASON_MARK: Record<AttentionTone, string> = { crimson: 'bg-signal-dang', amber: 'bg-signal-warn', navy: 'bg-ageing-3' };

/** What was last said, in one short line: the outcome, then the words. */
export function noteText(note: LastNote): string {
    const body = note.body.replace(/\s+/g, ' ').trim();
    const kind = ACTIVITY_LABELS[note.kind] ?? 'Note';
    if (!body) return kind;
    return note.kind === 'note' ? `“${body}”` : `${kind}: “${body}”`;
}

/** Who an account is with: its collector, else its owner, by name. */
export function assigneeName(row: BookRow, names: Map<string, string>): string | null {
    const who = (row.customer.collectorMemberId ?? row.customer.ownerMemberId) as string | undefined;
    return who ? names.get(who) ?? null : null;
}

/** What happens next on an account, as words, from the company's day: "Late 3 days", "Today", "26 Oct". */
export function nextFollowUp(row: BookRow, short: (d: Date | string) => string): { text: string; spoken: string; tone: FollowUpTone } {
    const { text, spoken, tone } = followUpWords(row.followUp, row.daysToFollowUp, row.customer.followUpDate ? short(row.customer.followUpDate) : '');
    return { text, spoken, tone };
}

/** When somebody last recorded anything on the account, as words. */
export function lastContact(value: Date | string | undefined, today: Date, short: (d: Date | string) => string): string {
    if (!value) return 'Never';
    const days = daysFromToday(value, today);
    if (days === null) return '—';
    if (days >= 0) return 'Today';
    if (days === -1) return 'Yesterday';
    if (days > -7) return `${-days} days ago`;
    return short(value);
}
